import { flatToReorderPayload, type FlatDraftItem } from './navigation-tree.utils';
import {
  createItem,
  createMenu,
  deleteItem,
  getNavTree,
  reorderItems,
  updateItem,
  updateMenu,
  type CreateItemInput,
} from './navigation.api';

const isTempId = (id: string): boolean => id.startsWith('temp-');

/**
 * Persist a WordPress-style draft: create menu if needed, delete removed items,
 * create new items (parents before children), patch changed fields, then reorder.
 */
export const saveMenuDraft = async (args: {
  location: string;
  menuId: string | null;
  menuTitle: string;
  items: FlatDraftItem[];
  serverItemIds: Set<string>;
}): Promise<string> => {
  const { location, menuTitle, items, serverItemIds } = args;
  let menuId = args.menuId;

  if (!menuId) {
    const tree = await getNavTree(location);
    menuId = tree.menuId;
  }

  if (!menuId) {
    menuId = (await createMenu(location, menuTitle)).id;
  } else {
    await updateMenu(menuId, menuTitle);
  }

  const active = items.filter((i) => !i.isDeleted);
  const activeIds = new Set(active.map((i) => i.id));

  for (const id of serverItemIds) {
    if (!activeIds.has(id)) {
      await deleteItem(id);
    }
  }

  const layout = flatToReorderPayload(items);
  const idMap = new Map<string, string>();

  for (const row of layout) {
    if (!isTempId(row.id)) {
      idMap.set(row.id, row.id);
    }
  }

  for (const row of layout) {
    if (!isTempId(row.id)) continue;
    const draft = active.find((i) => i.id === row.id);
    if (!draft) continue;

    const parentId = row.parentId
      ? (idMap.get(row.parentId) ?? (isTempId(row.parentId) ? null : row.parentId))
      : null;

    const payload: CreateItemInput = {
      menuId,
      parentId,
      label: draft.label,
      href: draft.href,
      pageId: draft.pageId,
      openInNewTab: draft.openInNewTab,
      isVisible: draft.isVisible,
      sortOrder: row.sortOrder,
    };

    const created = await createItem(payload);
    idMap.set(row.id, created.id);
  }

  for (const row of layout) {
    if (isTempId(row.id)) continue;
    const draft = active.find((i) => i.id === row.id);
    if (!draft) continue;

    const parentId = row.parentId
      ? (idMap.get(row.parentId) ?? (isTempId(row.parentId) ? null : row.parentId))
      : null;

    await updateItem(row.id, {
      label: draft.label,
      href: draft.href,
      pageId: draft.pageId,
      openInNewTab: draft.openInNewTab,
      isVisible: draft.isVisible,
      parentId,
      sortOrder: row.sortOrder,
    });
  }

  const reorderPayload = layout.map((row) => ({
    id: idMap.get(row.id) ?? row.id,
    parentId: row.parentId ? (idMap.get(row.parentId) ?? row.parentId) : null,
    sortOrder: row.sortOrder,
  }));

  await reorderItems(menuId, reorderPayload);
  return menuId;
};
